import { useContext } from "react";
import { ColorValue, StyleProp, View, ViewStyle } from "react-native";
import Loading from "src/components/Loading";
import Touchable from "src/components/Touchable";
import useStyle from "src/hooks/useStyle";
import { IconComponent } from "src/icons";
import { color } from "src/styles";
import { paddingSmall } from "src/styles/spacing";
import { Text } from "src/swsh-native";
import { ShareContext } from "../../contexts/ShareContext";

export type ShareItemProps = {
	onPress?: () => void;
	disabled?: boolean;
	loading?: boolean;
	containerStyle?: StyleProp<ViewStyle>;
	name?: string;
	displayIconText?: boolean;
} & ({ Icon: IconComponent; backgroundColor?: ColorValue } | { IconFull: IconComponent });

export const SHARE_ITEM_SIZE = 48;
const ShareItem = ({
	onPress,
	disabled,
	loading,
	containerStyle,
	name,
	displayIconText: displayIconTextProp,
	...props
}: ShareItemProps) => {
	const { theme } = useStyle();
	const context = useContext(ShareContext);
	const displayIconText = displayIconTextProp ?? context?.displayIconText ?? true;

	if ("Icon" in props) {
		const { Icon = color.ShareItemBackground[theme] } = props;
		return (
			<Touchable
				containerStyle={[containerStyle]}
				onPress={onPress}
				disabled={disabled || loading}
				style={{
					alignItems: "center",
				}}
				hitSlop={10}
			>
				<View
					style={{
						width: SHARE_ITEM_SIZE,
						height: SHARE_ITEM_SIZE,
						alignItems: "center",
						justifyContent: "center",
						borderRadius: SHARE_ITEM_SIZE / 2,
					}}
				>
					{loading ? (
						<Loading size={24} />
					) : (
						<Icon
							size={24}
							color={!disabled ? color.Text[theme] : color.TextLight[theme]}
						/>
					)}
				</View>
				{displayIconText && name ? (
					<>
						{/* Sizing spacer. If we use the real component here it pushes things out horizontally */}
						<Text
							xs
							semibold
							style={{
								marginTop: paddingSmall,
							}}
						>
							{" "}
						</Text>
						<Text
							xs
							semibold
							style={{
								position: "absolute",
								bottom: 0,
								width: 100,
								textAlign: "center",
							}}
						>
							{name}
						</Text>
					</>
				) : null}
			</Touchable>
		);
	}
	if ("IconFull" in props) {
		const { IconFull } = props;
		return (
			<Touchable
				containerStyle={[
					containerStyle,
					{
						opacity: disabled || loading ? 0.5 : 1,
					},
				]}
				style={{
					alignItems: "center",
				}}
				onPress={onPress}
				disabled={disabled || loading}
				hitSlop={{
					top: 10,
					bottom: 10,
					left: 10,
					right: 10,
				}}
			>
				<IconFull size={SHARE_ITEM_SIZE} />
				{displayIconText && name ? (
					<>
						{/* Sizing spacer. If we use the real component here it pushes things out horizontally */}
						<Text
							xs
							style={{
								marginTop: paddingSmall,
							}}
						>
							{" "}
						</Text>
						<Text
							xs
							style={{
								position: "absolute",
								bottom: 0,
								width: 100,
								textAlign: "center",
							}}
						>
							{name}
						</Text>
					</>
				) : null}
			</Touchable>
		);
	}
	throw new Error("Missing Icon and IconFull");
};
export default ShareItem;
